import { Alert, Anchor, Breadcrumbs, Container, Divider, Group, Space, Text } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AgencyTemplateDTO } from '../../../../Types/MessageT';
import { AgencyTemplateS } from '../../../../Service/AgencyTemplateS';
import { BaseInput, RichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import Attachments from '../../../../Organismns/Attachments';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { openAttachmentSelectionModal } from '../../../../Atoms/AttachmentSelectionModal';
import TemplateActions from './AgencyItemActions';
import { IconAlertCircle } from '@tabler/icons';
import { TiptapS } from '../../../../Service/TiptapS';

const AgencyTemplateEditor = () => {
  const { id } = useParams();
  const { profileId } = useJwtClaims();
  const [template, setTemplate] = useState<AgencyTemplateDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      setLoading(true);
      AgencyTemplateS.getById(id)
        .then(setTemplate)
        .finally(() => setLoading(false));
    }
  }, [id]);

  const updateMessage = async (patch: Partial<AgencyTemplateDTO>) => {
    if (id) {
      const resJSON = await AgencyTemplateS.edit(id, patch);
      if (resJSON) {
        handleChange(resJSON);
        return true;
      }
    }
    return false;
  };

  const handleChange = (patch: Partial<AgencyTemplateDTO>) => {
    setTemplate((prev) => {
      if (prev) {
        return { ...prev, ...patch };
      }
      return null;
    });
  };

  const manageAttachment = () =>
    openAttachmentSelectionModal({
      profileId,
      initialAttachmnets: template ? template.attachments : [],
      onSave: async (attachments) => {
        const resJSON = await AgencyTemplateS.edit(id ?? '', { attachments });
        if (resJSON) {
          handleChange({ attachments });
          return true;
        }
        return false;
      },
    });

  const deleteFileAttachment = (index: number) => {
    if (template) {
      const tmpFiles = template.attachments;
      tmpFiles.splice(index, 1);
      updateMessage({ attachments: tmpFiles });
    }
  };

  return (
    <Container p={0}>
      <Group position="apart">
        <Breadcrumbs sx={{ overflow: 'hidden' }}>
          <Anchor onClick={() => navigate('/settings/agency/messages')}>Agenturvorlagen</Anchor>
          <Anchor sx={{ pointerEvents: 'none' }} className="overflow-ellipsis">
            <b>{loading ? 'Laden ...' : template?.name}</b>
          </Anchor>
        </Breadcrumbs>
        {template && (
          <TemplateActions
            template={template}
            onUpdate={handleChange}
            onDelete={() => navigate('/settings/agency/messages')}
          />
        )}
      </Group>
      <Divider my="xs" />
      <Space h="sm" />
      <Alert
        icon={<IconAlertCircle />}
        title={<Text>Wichtig: Verwende hier NICHT die Platzhalter für Event-Datum und Uhrzeit</Text>}
        sx={(theme) => ({ backgroundColor: theme.colors.lightBlue[0] })}
      >
        <Text>
          Diese Platzhalter beziehen sich auf den primären Event-Zeitraum, der nicht zwingend der selbe sein muss, wie
          der angefragte Event-Zeitraum (wenn du mehrere Event-Zeiträume im Event nutzt). Das Datum und die Uhrzeit der
          angefragten Event-Tage wird automatisch in der E-Mail beigefügt.
        </Text>
      </Alert>
      <Space h={'md'} />
      {template && (
        <BaseInput
          small
          value={template.subject}
          label="Betreff"
          description="help.message.email.pane"
          patchField={(value) => updateMessage({ subject: value })}
          toolbar={<BaseToolbar />}
        />
      )}
      <Space h="sm" />
      <Attachments
        attachments={template?.attachments}
        documentBlueprints={[]}
        deleteFileAttachment={deleteFileAttachment}
        deleteDocumentAttachment={undefined}
        eventId={undefined}
      />
      <Space h="sm" />
      {template && (
        <RichInput
          value={template.message}
          label="Nachricht"
          patchField={(value) => updateMessage({ message: value })}
          styles={TiptapS.TextAreaStyles}
          toolbar={
            <TemplateMessageToolbar
            // addAttachment={manageAttachment}
            />
          }
        />
      )}
    </Container>
  );
};

export default AgencyTemplateEditor;
